<script setup lang="ts">
import { onMounted, shallowRef, ref } from "vue";
import { AgGridVue } from "ag-grid-vue3";
import {
  themeQuartz,
  type GridApi,
  type GridOptions,
  type GridReadyEvent,
} from "ag-grid-enterprise";
import {useCharterDailyDeparturesStore} from "@/charter/stores/charter-daily-departures-store.ts";

const charterDailyDeparturesStore = useCharterDailyDeparturesStore();

const myTheme = themeQuartz.withParams({});
const selectedDate = ref<Date | undefined>(new Date());

const gridOptions: GridOptions = {
  pagination: true,
  autoSizeStrategy: {
    type: "fitGridWidth",
    defaultMinWidth: 100,
  },
  defaultColDef: {
    resizable: false,
  },
  columnDefs: [
    { field: "departureDate", headerName: "Date", filter: true },
    { field: "aircraftType", headerName: "A/C", filter: true },
    { field: "seats", filter: true },
    { field: "client", filter: true },
    { field: "fltNr", filter: true },
    { field: "stretch", filter: true },
    { field: "std", filter: true },
    { field: "sta", filter: true },
  ],
};

const gridApi = shallowRef<GridApi | null>(null);

onMounted(async () => {
  await charterDailyDeparturesStore.fetchDailyDepartures(selectedDate.value);
});

function onGridReady(params: GridReadyEvent) {
  gridApi.value = params.api;
}

function exportData() {
  if (!gridApi.value) {
    return;
  }

  const date = new Date();
  gridApi.value.exportDataAsExcel({
    fileName: "charter-daily-departures_" + date.toLocaleString()
  });
}

function refreshData() {
  charterDailyDeparturesStore.fetchDailyDepartures(selectedDate.value);
}

function onDateChange() {
  charterDailyDeparturesStore.fetchDailyDepartures(selectedDate.value);
}
</script>

<template>
  <div class="flex h-full flex-col gap-4">
    <div class="flex items-center justify-between">
      <h1>Daily departures</h1>
      <div class="flex items-center gap-2">
        <p-calendar
          v-model="selectedDate"
          @date-select="onDateChange"
          @clear-click="onDateChange"
          showIcon
          showButtonBar
          iconDisplay="input"
          placeholder="Select date"
        />
        <p-button
          link
          @click="refreshData"
          :loading="charterDailyDeparturesStore.isLoading"
        >
          <span class="material-icons" title="refresh">refresh</span>
        </p-button>
        <p-button
          label="Download"
          icon="pi pi-download"
          link
          @click="exportData"
          :disabled="charterDailyDeparturesStore.isLoading || !gridApi"
        >
          <span class="material-icons" title="download">download</span>
        </p-button>
      </div>
    </div>

    <div v-if="charterDailyDeparturesStore.hasError" class="p-message p-message-error">
      <div class="p-message-wrapper">
        <span class="p-message-icon pi pi-times-circle"></span>
        <div class="p-message-text">
          {{ charterDailyDeparturesStore.error }}
        </div>
        <button
          class="p-message-close p-link"
          @click="charterDailyDeparturesStore.clearError"
        >
          <span class="pi pi-times"></span>
        </button>
      </div>
    </div>

    <AgGridVue
      :rowData="charterDailyDeparturesStore.dailyDepartures"
      :loading="charterDailyDeparturesStore.isLoading"
      :gridOptions="gridOptions"
      ref="gridRef"
      :theme="myTheme"
      style="width: 100%; height: 100%"
      @grid-ready="onGridReady"
    />
  </div>
</template>

<style scoped lang="scss">
.p-message {
  margin: 0;
}

.p-calendar {
  min-width: 200px;
}
</style>
